import * as React from "react";
import { format, parse, isValid } from "date-fns";
import { Calendar as CalendarIcon, ChevronLeft, ChevronRight } from "lucide-react";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { Calendar } from "@/components/ui/calendar";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

/**
 * DatePicker - A fancy date picker with month/year navigation
 */
export function DatePicker({
  value,
  onChange,
  placeholder = "Pick a date",
  className,
  minYear = 1950,
  maxYear = new Date().getFullYear() + 10,
  disabled = false,
}) {
  const [open, setOpen] = React.useState(false);
  const [month, setMonth] = React.useState(value ? new Date(value) : new Date());

  const years = React.useMemo(() => {
    const result = [];
    for (let i = maxYear; i >= minYear; i--) {
      result.push(i);
    }
    return result;
  }, [minYear, maxYear]);

  const months = [
    "January", "February", "March", "April", "May", "June",
    "July", "August", "September", "October", "November", "December"
  ];

  const handleSelect = (date) => {
    if (date) {
      onChange(format(date, "yyyy-MM-dd"));
      setOpen(false);
    }
  };

  const handleMonthChange = (monthIndex) => {
    const newDate = new Date(month);
    newDate.setMonth(parseInt(monthIndex));
    setMonth(newDate);
  };

  const handleYearChange = (year) => {
    const newDate = new Date(month);
    newDate.setFullYear(parseInt(year));
    setMonth(newDate);
  };

  const selectedDate = value ? parse(value, "yyyy-MM-dd", new Date()) : undefined;
  const displayValue = selectedDate && isValid(selectedDate) 
    ? format(selectedDate, "PPP") 
    : undefined;

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          disabled={disabled}
          className={cn(
            "w-full justify-start text-left font-normal h-11",
            !value && "text-muted-foreground",
            className
          )}
        >
          <CalendarIcon className="mr-2 h-4 w-4 text-blue-500" />
          {displayValue || <span>{placeholder}</span>}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-auto p-0" align="start">
        <div className="p-3 border-b flex items-center justify-between gap-2 bg-slate-50 dark:bg-slate-800">
          <Select value={month.getMonth().toString()} onValueChange={handleMonthChange}>
            <SelectTrigger className="w-[130px] h-9">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {months.map((m, i) => (
                <SelectItem key={m} value={i.toString()}>
                  {m}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select value={month.getFullYear().toString()} onValueChange={handleYearChange}>
            <SelectTrigger className="w-[90px] h-9">
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="max-h-[200px]">
              {years.map((y) => (
                <SelectItem key={y} value={y.toString()}>
                  {y}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <Calendar
          mode="single"
          selected={selectedDate}
          onSelect={handleSelect}
          month={month}
          onMonthChange={setMonth}
          initialFocus
          className="rounded-md"
        />
      </PopoverContent>
    </Popover>
  );
}

/**
 * TimePicker - A fancy time picker
 */
export function TimePicker({
  value,
  onChange,
  placeholder = "Pick a time",
  className,
  disabled = false,
  use24Hour = false,
}) {
  const [open, setOpen] = React.useState(false);

  const hours = React.useMemo(() => {
    const result = [];
    const max = use24Hour ? 24 : 12;
    const start = use24Hour ? 0 : 1;
    for (let i = start; i <= (use24Hour ? 23 : 12); i++) {
      result.push(i.toString().padStart(2, "0"));
    }
    return result;
  }, [use24Hour]);

  const minutes = React.useMemo(() => {
    const result = [];
    for (let i = 0; i < 60; i += 5) {
      result.push(i.toString().padStart(2, "0"));
    }
    return result;
  }, []);

  const parseTime = (timeStr) => {
    if (!timeStr) return { hour: "", minute: "", period: "AM" };
    const parts = timeStr.split(":");
    let hour = parseInt(parts[0] || "0");
    const minute = parts[1]?.substring(0, 2) || "00";
    let period = "AM";
    
    if (!use24Hour) {
      if (hour >= 12) {
        period = "PM";
        if (hour > 12) hour -= 12;
      }
      if (hour === 0) hour = 12;
    }
    
    return {
      hour: hour.toString().padStart(2, "0"),
      minute,
      period,
    };
  };

  const { hour, minute, period } = parseTime(value);

  const handleChange = (newHour, newMinute, newPeriod) => {
    let h = parseInt(newHour || hour || "12");
    const m = newMinute || minute || "00";
    const p = newPeriod || period;
    
    if (!use24Hour) {
      if (p === "PM" && h !== 12) h += 12;
      if (p === "AM" && h === 12) h = 0;
    }
    
    onChange(`${h.toString().padStart(2, "0")}:${m}`);
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          disabled={disabled}
          className={cn(
            "w-full justify-start text-left font-normal h-11",
            !value && "text-muted-foreground",
            className
          )}
        >
          <CalendarIcon className="mr-2 h-4 w-4 text-purple-500" />
          {value ? (
            <span>
              {hour}:{minute} {!use24Hour && period}
            </span>
          ) : (
            <span>{placeholder}</span>
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-auto p-4" align="start">
        <div className="flex items-center gap-2">
          <Select value={hour} onValueChange={(h) => handleChange(h, minute, period)}>
            <SelectTrigger className="w-[70px]">
              <SelectValue placeholder="HH" />
            </SelectTrigger>
            <SelectContent className="max-h-[200px]">
              {hours.map((h) => (
                <SelectItem key={h} value={h}>
                  {h}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <span className="text-xl font-bold">:</span>
          <Select value={minute} onValueChange={(m) => handleChange(hour, m, period)}>
            <SelectTrigger className="w-[70px]">
              <SelectValue placeholder="MM" />
            </SelectTrigger>
            <SelectContent className="max-h-[200px]">
              {minutes.map((m) => (
                <SelectItem key={m} value={m}>
                  {m}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          {!use24Hour && (
            <Select value={period} onValueChange={(p) => handleChange(hour, minute, p)}>
              <SelectTrigger className="w-[70px]">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="AM">AM</SelectItem>
                <SelectItem value="PM">PM</SelectItem>
              </SelectContent>
            </Select>
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
}

/**
 * DateTimePicker - Combined date and time picker
 */
export function DateTimePicker({
  value,
  onChange,
  datePlaceholder = "Pick a date",
  timePlaceholder = "Pick a time",
  className,
  disabled = false,
}) {
  const parseDateTime = (dateTimeStr) => {
    if (!dateTimeStr) return { date: "", time: "" };
    const parts = dateTimeStr.split("T");
    return {
      date: parts[0] || "",
      time: parts[1]?.substring(0, 5) || "",
    };
  };

  const { date, time } = parseDateTime(value);

  const handleDateChange = (newDate) => {
    onChange(time ? `${newDate}T${time}` : newDate);
  };

  const handleTimeChange = (newTime) => {
    onChange(date ? `${date}T${newTime}` : `${format(new Date(), "yyyy-MM-dd")}T${newTime}`);
  };

  return (
    <div className={cn("flex gap-2", className)}>
      <DatePicker
        value={date}
        onChange={handleDateChange}
        placeholder={datePlaceholder}
        disabled={disabled}
        className="flex-1"
      />
      <TimePicker
        value={time}
        onChange={handleTimeChange}
        placeholder={timePlaceholder}
        disabled={disabled}
        className="w-[140px]"
      />
    </div>
  );
}

export default DatePicker;
